import { CameraOutlined, SearchOutlined } from '@ant-design/icons';
import {
  awaitingReply,
  DOCUMENT_TYPE_LABELS,
  matchesWords,
  present,
  today,
} from '@horof/shared';
import { Alert, Button, Empty, Input, Segmented, Skeleton, Tag } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import { documentsContext, PhoneDocumentsDocument } from './documents-data';

type Filter = 'all' | 'letters' | 'waiting';

/**
 * «الوثائق» on the phone: find a document and open its pages, or scan a new
 * one («تصوير وثيقة»). The rest is on the desk.
 */
export function DocumentsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [{ data, error }, refetch] = useQuery({
    query: PhoneDocumentsDocument,
    context: documentsContext,
  });
  const now = today();
  const all = present(data?.associationDocuments?.nodes);
  const letters = all.filter(
    (d) => d.type === 'CORRESPONDENCE_IN' || d.type === 'CORRESPONDENCE_OUT',
  );
  const waiting = all.filter((d) => awaitingReply(d, now));
  const list =
    filter === 'letters' ? letters : filter === 'waiting' ? waiting : all;
  const visible = list.filter((d) =>
    matchesWords([d.title, d.reference, d.counterparty?.name], search),
  );

  return (
    <div className="page">
      <header className="list-header">
        <div className="title-row">
          <h1>الوثائق</h1>
        </div>
        <Link to="/documents/scan">
          <Button
            type="primary"
            block
            className="big-button"
            icon={<CameraOutlined />}
          >
            تصوير وثيقة
          </Button>
        </Link>
        <Input
          size="large"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="بحث: العنوان، المرجع، الجهة"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Segmented<Filter>
          block
          size="large"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'letters', label: 'المراسلات' },
            { value: 'waiting', label: 'تنتظر الرد' },
          ]}
        />
      </header>

      {error && (
        <Alert
          type="error"
          showIcon
          message="تعذّر تحميل الوثائق"
          action={
            <Button onClick={() => refetch({ requestPolicy: 'network-only' })}>
              أعد المحاولة
            </Button>
          }
        />
      )}
      {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}

      <ol className="member-list">
        {visible.map((d) => (
          <li key={d.id}>
            <Link to={`/documents/${d.id}`} className="member-row">
              <span className="member-main">
                <span className="member-name">{d.title}</span>
                <span className="member-sub">
                  {DOCUMENT_TYPE_LABELS[d.type]} ·{' '}
                  <span className="ltr-num">{d.documentDate}</span>
                  {d.reference && (
                    <>
                      {' · '}
                      <span className="ltr-num">{d.reference}</span>
                    </>
                  )}
                </span>
                {d.counterparty && (
                  <span className="member-sub">{d.counterparty.name}</span>
                )}
              </span>
              {awaitingReply(d, now) && <Tag color="orange">ينتظر الرد</Tag>}
              {d.isCurrent && <Tag color="green">المعتمدة</Tag>}
            </Link>
          </li>
        ))}
      </ol>
      {data && visible.length === 0 && (
        <Empty description={search ? 'لا وثيقة بهذا البحث' : 'لا وثائق بعد'} />
      )}
    </div>
  );
}
